import sortBy from "lodash/sortBy";
import upperFirst from "lodash/upperFirst";
import type {
  AnnotatedPlan,
  Annotation,
  AnnotationType,
  Node,
  NodeField,
  Plan,
} from "types/explain";
import {
  formatBytes,
  formatMs,
  formatNumber,
  formatPercent,
} from "utils/format";
import { Impact } from "ui/ImpactRenderer";
import pluralize from "pluralize";

export function isReferencedBy(
  nodeId: number | string,
  annotation: Annotation,
): boolean {
  return annotation?.nodes.some(
    (nodeRef) => String(nodeRef.id) === String(nodeId),
  );
}

export type QueryTuningInsight = {
  id: number;
} & (
  | {
      type: "NestedLoop";
      misestimate_factor: number;
      inner_table_id: number;
      proposed_index_id: number;
    }
  | {
      type: "OrderByLimit";
      index_used: string;
      filter_columns: string[];
      proposed_index_ids: number[];
      rows_removed_by_filter: number;
    }
  | {
      type: "InefficientIndexUse";
      index_used: string;
      filter_columns: string[];
      proposed_index_ids: number[];
      proposed_index_names?: string[];
      table_alias?: string;
      rows_removed_by_filter: number;
    }
  | {
      type: "WorkMemSpill";
      node_type: "Sort" | "Hash";
      sort_method?: string;
      hash_batches?: number;
      current_bytes?: number;
      needed_bytes?: number;
      maximum_bytes?: number;
    }
  | {
      type: "OrToUnion";
      join_type: string;
      join_filter: string;
      tables_referenced: string[];
      indexable_columns: { table?: string; column: string }[];
      proposed_index_ids?: number[];
    }
);

export type QuerySampleType = {
  id: string;
  occurredAt: number;
  runtimeMs: number;
  queryText: string;
  parameters?: string[];
};

export type ExplainPlanType = {
  id: string;
  humanId: string;
  seenAt: number;
  querySample?: QuerySampleType | null | undefined;
  query: {
    id: string;
  };
  format: string;
  fingerprint: string;
  outputText?: string;
  annotatedJson?: AnnotatedPlan;
  totalCost?: number;
  totalSharedBlksRead?: number;
  totalBlkReadTime?: number;
  advisorInsights?: QueryTuningInsight[];
};

export function combineInsights(
  plan: Plan,
  qaInsights: QueryTuningInsight[],
  annotations: Annotation[],
): Annotation[] {
  return qaInsights
    .map((insight: QueryTuningInsight, i) => {
      return transformNodeInsightToPlanAnnotation(insight, i, plan);
    })
    .filter((ann): ann is Annotation => ann !== null)
    .concat(
      sortBy(
        annotations.map((ann) => {
          return {
            ...ann,
            impact: getAnnotationImpact(ann),
          };
        }),
        (ann) => -ann.impact,
      ).map((ann, i) => {
        return {
          // generate ids for the other annotations
          id: i + qaInsights.length,
          ...ann,
        };
      }),
    );
}

function transformNodeInsightToPlanAnnotation(
  insight: QueryTuningInsight,
  id: number,
  plan: Plan,
): Annotation | null {
  const root = plan[0].Plan;
  const insightNode = findNode(root, insight.id);

  switch (insight.type) {
    case "NestedLoop": {
      const outer = insightNode["Plans"]?.find(
        (child) => child["Parent Relationship"] === "Outer",
      );
      const inner = insightNode["Plans"]?.find(
        (child) => child["Parent Relationship"] === "Inner",
      );

      if (!outer || !inner) {
        throw new Error("invalid plan");
      }

      const nodes = [
        { id: insightNode.extra.id, label: "nested loop" },
        { id: outer.extra.id, label: "outer" },
        { id: inner.extra.id, label: "inner" },
      ];

      return {
        id,
        nodes,
        type: "query advisor/inefficient nested loop",
        impact: 4,
        detail: "",
      };
    }
    case "OrderByLimit": {
      return {
        id,
        nodes: [
          {
            id: insight.id,
            label: "",
          },
        ],
        type: "query advisor/order by limit",
        impact: 5,
        detail: "",
      };
    }
    case "WorkMemSpill": {
      return {
        id,
        nodes: [
          {
            id: insight.id,
            label: "",
          },
        ],
        type: "query advisor/work mem spill",
        impact: 4,
        detail: "",
      };
    }
    case "InefficientIndexUse": {
      return {
        id,
        nodes: [
          {
            id: insight.id,
            label: "",
          },
        ],
        type: "query advisor/inefficient index use",
        impact: 4,
        detail: "",
      };
    }
    case "OrToUnion": {
      return {
        id,
        nodes: [
          {
            id: insight.id,
            label: "",
          },
        ],
        type: "query advisor/late join filter with or",
        impact: 4,
        detail: "",
      };
    }
    default:
      throw new Error("unknown insight type: " + insight);
  }
}

export function getNodeAnnotations(nodeId: number, annotations: Annotation[]) {
  return annotations.filter((a) =>
    a.nodes.some((nodeRef) => nodeRef.id === nodeId),
  );
}

export type DescribedAnnotation = {
  annotation: Annotation;
  title: React.ReactNode;
  details: React.ReactNode[];
};

export function describeAnnotation(
  annotation: Annotation,
  plan: Plan,
): DescribedAnnotation {
  switch (annotation.type) {
    case "query advisor/inefficient nested loop":
    case "query advisor/order by limit":
    case "query advisor/work mem spill":
    case "query advisor/inefficient index use":
    case "query advisor/late join filter with or": {
      const node = annotation.nodes[0];
      return {
        annotation,
        title: getAnnotationTitle(annotation.type),
        details: getInsightDetails(annotation.type, node.id, plan),
      };
    }
    default:
      return {
        annotation,
        title: getAnnotationTitle(annotation.type),
        details: [annotation.detail],
      };
  }
}

export const getInsightDetails = (
  type: string,
  nodeId?: number,
  plan?: Plan,
) => {
  switch (type) {
    case "NestedLoop":
    case "query advisor/inefficient nested loop":
      if (plan) {
        const root = plan[0].Plan;
        const insightNode = findNode(root, nodeId);
        const totRuntime = plan.at(0).Plan["Actual Total Time"];

        const outer = insightNode["Plans"]?.find(
          (child) => child["Parent Relationship"] === "Outer",
        );
        const inner = insightNode["Plans"]?.find(
          (child) => child["Parent Relationship"] === "Inner",
        );

        if (!outer || !inner) {
          throw new Error("invalid plan");
        }

        const estLoops = outer["Plan Rows"];
        const actualLoops = inner["Actual Loops"];
        const details = [
          `Estimated ${pluralize(
            "loops",
            estLoops,
            true,
          )}, but ${actualLoops} were executed`,
        ];
        if (totRuntime) {
          const innerTime = getCumulativeTotalMetric(
            inner,
            "Actual Total Time",
          );

          details.push(
            `Inner side of this nested loop takes ${formatMs(
              innerTime,
            )} (${formatPercent(innerTime / totRuntime, 0)} of total)`,
          );
        }
        return details;
      } else {
        return getInsightDescription(type);
      }
    case "OrderByLimit":
    case "query advisor/order by limit":
    case "InefficientIndexUse":
    case "query advisor/inefficient index use":
      if (plan) {
        const root = plan[0].Plan;
        const insightNode = findNode(root, nodeId);
        const details = [`The index ${insightNode["Index Name"]} was used`];
        details.push(
          `${formatNumber(
            insightNode["Rows Removed by Filter"] as number,
          )} rows were removed by filter after the index scan`,
        );
        return details;
      } else {
        return getInsightDescription(type);
      }
    case "OrToUnion":
    case "query advisor/late join filter with or":
      return getInsightDescription(type);
    case "WorkMemSpill":
    case "query advisor/work mem spill":
      if (plan) {
        const root = plan[0].Plan;
        const insightNode = findNode(root, nodeId);
        const nodeType = insightNode["Node Type"];
        const details: string[] = [];

        if (nodeType === "Sort") {
          const method = insightNode["Sort Method"];
          const spaceKb = insightNode["Sort Space Used"] as number;
          details.push(
            method
              ? `Sort didn't fit in work_mem, spilled to disk using ${method} method`
              : `Sort didn't fit in work_mem and spilled to disk`,
          );
          if (spaceKb) {
            details.push(`Wrote ${formatBytes(spaceKb * 1024)} of sort data`);
          }
        } else if (nodeType === "Hash") {
          const batches = insightNode["Hash Batches"] as number;
          const peakKb = insightNode["Peak Memory Usage"] as number;
          details.push(
            `Hash table didn't fit in work_mem, processed in ${batches} batches`,
          );
          if (peakKb) {
            details.push(
              `Each batch used up to ${formatBytes(peakKb * 1024)} of memory`,
            );
          }
        }
        return details;
      } else {
        return getInsightDescription(type);
      }
    default:
      return [];
  }
};

export function getInsightDescription(type: string): string[] {
  switch (type) {
    case "NestedLoop":
    case "query advisor/inefficient nested loop":
      return [
        "Found a Nested Loop with a high row misestimate on the outer table.",
        "Try rewriting the query with a materialized CTE to use a different query plan.",
      ];
    case "OrderByLimit":
    case "query advisor/order by limit":
      return [
        "The ORDER BY + LIMIT clause is causing an inefficient index to be selected by the planner.",
        "Try rewriting the query by adding +0 to the ORDER BY column to use a different index scan.",
      ];
    case "WorkMemSpill":
    case "query advisor/work mem spill":
      return [
        "A query operation spilled to disk because it exceeded work_mem.",
        "Try raising work_mem locally for this query, per-query using a pg_hint_plan Set() hint, or globally for the specific user, database, or entire server.",
      ];
    case "InefficientIndexUse":
    case "query advisor/inefficient index use":
      return [
        "An index scan is reading many rows that are later discarded by a filter, suggesting a different index would be a better match for this query.",
        "Try using a pg_hint_plan hint to force the planner to use a different existing index.",
      ];
    case "OrToUnion":
    case "query advisor/late join filter with or":
      return [
        "A join has an OR condition, parts of which each filter a different table, stopping Postgres from using indexes to satisfy the join.",
        "Try rewriting the query as a UNION of the individual conditions so each side can use its own index.",
      ];
  }
  return [];
}

export function getInsightLink(type: string): string {
  switch (type) {
    case "NestedLoop":
    case "query advisor/inefficient nested loop":
      return "https://pganalyze.com/docs/query-advisor/insights/inefficient-nested-loops";
    case "OrderByLimit":
    case "query advisor/order by limit":
      return "https://pganalyze.com/docs/query-advisor/insights/wrong-index-order-by";
    case "WorkMemSpill":
    case "query advisor/work mem spill":
      return "https://pganalyze.com/docs/query-advisor/insights/disk-spill-work-mem";
    case "InefficientIndexUse":
    case "query advisor/inefficient index use":
      return "https://pganalyze.com/docs/query-advisor/insights/inefficient-index-use";
    case "OrToUnion":
    case "query advisor/late join filter with or":
      return "https://pganalyze.com/docs/query-advisor/insights/late-join-filter-with-or";
  }
  return "https://pganalyze.com/docs/query-advisor/insights";
}

export function getAnnotationTitle(type: AnnotationType | string): string {
  switch (type) {
    case "query advisor/inefficient nested loop":
      return "Inefficient Nested Loop";
    case "query advisor/order by limit":
      return "Wrong Index Due To ORDER BY";
    case "query advisor/work mem spill":
      return "Disk Spill Due To Low work_mem";
    case "query advisor/inefficient index use":
      return "Inefficient Index Use";
    case "query advisor/late join filter with or":
      return "Late Join Filter with OR";
    case "disk sort":
      return "Sort Spills to Disk";
    case "hash batches":
      return "Too Many Hash Batches";
    case "inefficient index":
      return "Inefficient Index Scan";
    case "inefficient nested loop":
      return "Inefficient Nested Loop Join";
    case "large offset":
      return "LIMIT with large OFFSET";
    case "lossy bitmaps":
      return "Inefficient Bitmap scan";
    case "mis-estimate":
      return "Significant mis-estimate";
    case "slow scan":
      return "Inefficient Sequential Scan";
    case "stale stats":
      return "Stale Statistics";
    default:
      return type
        .split(" ")
        .map((word) => upperFirst(word))
        .join(" ");
  }
}

export function getAnnotationImpact(ann: Annotation): Impact {
  switch (ann.type) {
    case "query advisor/inefficient nested loop":
    case "query advisor/order by limit":
    case "query advisor/work mem spill":
    case "query advisor/inefficient index use":
    case "query advisor/late join filter with or":
      return 5;
    case "disk sort":
    case "hash batches":
      return 4;
    case "inefficient index":
    case "inefficient nested loop":
    case "large offset": // TODO: merge with query advisor/inefficient nested loop
      return 3;
    case "lossy bitmaps":
    case "stale stats":
    case "slow scan":
      return 2;
    case "mis-estimate":
      return 1;

    default:
      return 1;
  }
}

export function insightTypeToAnnotationType(insightType: string): string {
  switch (insightType) {
    case "NestedLoop":
      return "query advisor/inefficient nested loop";
    case "OrderByLimit":
      return "query advisor/order by limit";
    case "WorkMemSpill":
      return "query advisor/work mem spill";
    case "InefficientIndexUse":
      return "query advisor/inefficient index use";
    case "OrToUnion":
      return "query advisor/late join filter with or";
    default:
      return insightType;
  }
}

export function reduce<T>(
  node: Node,
  callback: (state: T, node: Node, depth: number) => T,
  initialState: T,
): T {
  function visit(state: T, node: Node, depth: number): T {
    let newState = callback(state, node, depth);
    if (node.Plans) {
      newState = node.Plans.reduce((childState, child) => {
        return visit(childState, child, depth + 1);
      }, newState);
    }
    return newState;
  }

  return visit(initialState, node, 1);
}

export function matchesId(node: Node, nodeId: string | number): boolean {
  return (
    String(node?.extra.id) === String(nodeId) ||
    node?.["Subplan Name"] == `CTE ${nodeId}`
  );
}

export function findNode(
  subplan: Plan | Node | undefined,
  nodeId: string | number,
): Node {
  if (!subplan) {
    return undefined;
  }
  if (Array.isArray(subplan)) {
    return subplan.reduce<Node>((result, plan) => {
      if (result) {
        return result;
      }
      return findNode(plan.Plan, nodeId);
    }, undefined);
  }
  return reduce<Node>(
    subplan,
    (result, curr) => {
      if (matchesId(curr, nodeId)) {
        return curr;
      }
      return result;
    },
    undefined,
  );
}

export function getNodeIds(subplan: Node) {
  return reduce<Set<number>>(
    subplan,
    (result, curr) => {
      result.add(curr.extra.id);
      return result;
    },
    new Set<number>(),
  );
}

export function enrichPlan(plan: Plan) {
  const root = structuredClone(plan[0].Plan);
  let nodeId = 1;
  function visit(
    node: Node,
    {
      location,
      estimatedLoops,
      workersLaunched,
    }: {
      location: number[];
      estimatedLoops?: number;
      workersLaunched?: number;
    },
  ) {
    node.extra = {
      // If we have other `extra` info already, preserve it, but override any
      // duplicate fields with our own
      ...node.extra,
      id: nodeId++,
      location,
      estimatedLoops,
      workersLaunched,
    };
    let outerPlanRows: number = undefined;
    if (node["Node Type"] === "Nested Loop") {
      outerPlanRows = node.Plans?.find(
        (child) => child["Parent Relationship"] === "Outer",
      )?.["Plan Rows"];
    }
    node.Plans?.forEach((child, i) => {
      visit(child, {
        location: location.concat(i),
        estimatedLoops:
          child["Parent Relationship"] === "Inner" ? outerPlanRows : undefined,
        workersLaunched: node["Workers Launched"] ?? node.extra.workersLaunched,
      });
    });
    return node;
  }
  return [{ Plan: visit(root, { location: [0] }) }];
}

export function planDepth(subPlan: Node): number {
  return reduce(
    subPlan,
    (maxDepth, _, depth) => {
      return Math.max(depth, maxDepth);
    },
    0,
  );
}

export function findPlanIndexes(plan: Plan): Set<string> {
  return reduce(
    plan[0].Plan,
    (allIndexes, node) => {
      const index = node["Index Name"];
      if (index) {
        allIndexes.add(index);
      }
      return allIndexes;
    },
    new Set<string>(),
  );
}

export type PreparedPlan = {
  root: Node;
  subPlans: Node[];
};

export function extractSubplans(plan: Plan): PreparedPlan {
  // N.B.: We use structuredClone here because we mutate the plan while walking it
  const root = structuredClone(plan[0].Plan);
  const subPlans = [] as Node[];
  function doExtractSubplans(node: Node): void {
    if (!("Plans" in node)) {
      return;
    }
    const mainChildren = node["Plans"].filter((p) => {
      if (["InitPlan", "SubPlan"].includes(p["Parent Relationship"])) {
        p.extra["subplan_parent_id"] = node.extra.id;
        subPlans.push(p);
        return false;
      }
      return true;
    });

    node["Plans"].forEach((p) => doExtractSubplans(p));

    node["Plans"] = mainChildren;
  }

  doExtractSubplans(root);

  return {
    root,
    subPlans,
  };
}

export function getNodeLoops(node: Node, isEstimate: boolean): number {
  return (isEstimate ? node.extra.estimatedLoops : node["Actual Loops"]) ?? 1;
}

export function getFieldLoopMultiplier(node: Node, field: NodeField): number {
  if (!isPerLoopField(field)) {
    return 1;
  }
  const isEstimate = isEstimateField(field);

  return getNodeLoops(node, isEstimate);
}

export function getWorker(node: Node, workerId: number) {
  // It is possible for a worker to do no work (because its loops is zero).
  // In that case, its work is not reported (see https://github.com/postgres/postgres/blob/2e947217474c15c7fd9011d1ab2b0d4657b3eae2/src/backend/commands/explain.c#L2303 ),
  // so its entry in the Workers array will be missing, and we can return undefined here
  return node.Workers?.find((w) => w["Worker Number"] === workerId);
}

function hasWorkerDetails(node: Node) {
  return node.Workers?.length > 0;
}

function getWorkerCount(node: Node) {
  return node.extra.workersLaunched ?? 0;
}

function getWorkerLoops(node: Node, workerId: number) {
  const workerCount = getWorkerCount(node);
  if (workerCount === 0) {
    return 0;
  }
  if (!hasWorkerDetails(node)) {
    // If we have no detailed information, assume this worker did an equal share
    // of the loops
    return (node["Actual Loops"] ?? 1) / (workerCount + 1) /* leader */;
  }

  const worker = getWorker(node, workerId);
  return worker?.["Actual Loops"] ?? 1;
}

export function getLeaderLoops(node: Node): number {
  const totLoops = node["Actual Loops"] ?? 1;
  if (!hasWorkerDetails(node)) {
    // If we have no detailed information, assume the leader did an equal share
    // of the loops
    return totLoops / (getWorkerCount(node) + 1) /* leader */;
  }

  const totWorkerLoops = node.Workers.reduce((tot, w) => {
    return tot + getWorkerLoops(node, w["Worker Number"]);
  }, 0);
  return totLoops - totWorkerLoops;
}

/**
 * Postgres records information in an EXPLAIN plan in a manner that reflects the
 * planning and execution process. While this makes sense in terms of
 * observability, these raw metrics can be hard to work with for plan readers.
 *
 * The following node metric getter functions support calculating various
 * metrics that are more likely to be useful to a user interpreting a plan.
 *
 * Understanding a few things about EXPLAIN plans is helpful to follow the logic
 * here:
 *
 *  - By default, all metrics are cumulative (include metrics of children).
 *  - When loops are involved (e.g., for the inner side of a Nested Loop node),
 *    most metrics are reported as a total across all loop iterations, but some
 *    are a per-loop average (see `isPerLoopField` below).
 *  - Loop estimates are not recorded directly: they must be inferred from the
 *    Nested Loop node's outer child row estimate ("Plan Rows").
 *  - When parallelism is involved (see
 *    https://www.postgresql.org/docs/current/parallel-query.html), the parallel
 *    subtree will have an "Actual Loops" multiplier based on the number of
 *    workers. This composes with any actual looping that may occur.
 *  - Parallel workers are not planned in detail: only the desired worker count
 *    is ("Workers Planned").
 *  - Parallel workers do not always have execution details available (in these
 *    cases, we assume an equal split of work occurred).
 *  - The worker loops multiplier means metrics are reported for all parallel
 *    workers plus the leader together at the top level of each node. If worker
 *    details are available, the metrics for the leader itself can be calculated
 *    by subtracting the total worker metrics.
 *
 * The functions below use a naming convention of combining several facets of
 * each aspect of the metric being requested:
 *
 * E.g., in `getCumulativeWorkerTotalMetric`:
 *
 *  - child cost accounting: "cumulative" (including children) or "self" (only
 *    costs accrued directly by the node)
 *  - parallelism accounting: empty string (overall metric, summarizing over
 *    parallelism), "leader" (only costs of the leader), and "worker" (costs for
 *    a specific worker)
 *  - loop accounting: "total" (across all loop iterations) and "avg" (per loop)
 *
 * In addition to these, the `MaxParallel` functions are useful for metrics that
 * incur cost in parallel. E.g., since the "Actual Total Time" spent by the
 * leader and workers is consumed in parallel, it makes more sense to take the
 * max rather than to sum these.
 *
 */

export function getCumulativeTotalMetric(node: Node, field: NodeField): number {
  if (!(field in node)) {
    return undefined;
  }
  const rawValue = node[field] as number;
  const loopMultiplier = getFieldLoopMultiplier(node, field);

  return rawValue * loopMultiplier;
}

export function getCumulativeAvgMetric(node: Node, field: NodeField): number {
  if (!(field in node)) {
    return undefined;
  }
  const total = getCumulativeTotalMetric(node, field);
  const isEstimate = isEstimateField(field);
  const nodeLoops = getNodeLoops(node, isEstimate);
  return total / nodeLoops;
}

export function getCumulativeWorkerTotalMetric(
  node: Node,
  field: NodeField,
  workerId: number,
) {
  if (!(field in node)) {
    return undefined;
  }
  if (isEstimateField(field)) {
    throw new Error("Estimate metrics are not available for workers");
  }
  const workerCount = getWorkerCount(node);
  if (workerCount === 0) {
    return 0;
  }

  if (!hasWorkerDetails(node)) {
    const value = getCumulativeTotalMetric(node, field);
    return value / workerCount;
  }

  const worker = getWorker(node, workerId);
  const rawValue = (worker?.[field as keyof typeof worker] ?? 0) as number;
  const workerLoops = worker?.["Actual Loops"] ?? 1;
  const workerLoopMultiplier = isPerLoopField(field) ? workerLoops : 1;
  return rawValue * workerLoopMultiplier;
}

export function getCumulativeWorkerAvgMetric(
  node: Node,
  field: NodeField,
  workerId: number,
) {
  if (!(field in node)) {
    return undefined;
  }
  const workerCount = getWorkerCount(node);
  if (workerCount === 0) {
    return 0;
  }
  const workerTotalMetric = getCumulativeWorkerTotalMetric(
    node,
    field,
    workerId,
  );
  const workerLoops = getWorkerLoops(node, workerId);

  return workerTotalMetric / workerLoops;
}

export function getCumulativeLeaderTotalMetric(node: Node, field: NodeField) {
  if (!(field in node)) {
    return undefined;
  }
  const total = getCumulativeTotalMetric(node, field);
  const workerCount = getWorkerCount(node);
  if (workerCount === 0) {
    // If parallelism is not involved, the leader value is just the node total
    return total;
  }

  if (!hasWorkerDetails(node)) {
    // If we have no detailed worker information, assume the leader (and all
    // workers) performed the same amount of work and attribute an equal share
    // of the metric to them.
    return total / (workerCount + 1) /* leader */;
  }

  const workerTotal = node.Workers.reduce((tot, w) => {
    const workerLoops = getWorkerLoops(node, w["Worker Number"]);
    const workerLoopMultiplier = isPerLoopField(field) ? workerLoops : 1;
    const workerValue = w[field as keyof typeof w] as number;
    return tot + workerValue * workerLoopMultiplier;
  }, 0);
  return total - workerTotal;
}

export function getCumulativeLeaderAvgMetric(node: Node, field: NodeField) {
  if (!(field in node)) {
    return undefined;
  }
  const leaderTotal = getCumulativeLeaderTotalMetric(node, field);
  const leaderLoops = getLeaderLoops(node);

  return leaderTotal / leaderLoops;
}

export function getMaxParallelCumulativeTotalMetric(
  node: Node,
  field: NodeField,
) {
  if (!(field in node)) {
    return undefined;
  }
  const workerCosts =
    node.Workers?.map((w) => {
      return getCumulativeWorkerTotalMetric(node, field, w["Worker Number"]);
    }) ?? [];
  const leaderCost = getCumulativeLeaderTotalMetric(node, field);

  return Math.max(leaderCost, ...workerCosts);
}

export function getSelfTotalMetric(node: Node, field: NodeField): number {
  if (!(field in node)) {
    return undefined;
  }
  const cumulativeTotal = getCumulativeTotalMetric(node, field);
  const childrenCost =
    node.Plans?.reduce((tot, child) => {
      const childCost = getCumulativeTotalMetric(child, field);
      return tot + (childCost ?? 0);
    }, 0) ?? 0;

  // Some nodes like Limit may have a cumulative total cost that's *lower* than
  // the child cost: in that case, clamp self-cost to zero.
  return Math.max(cumulativeTotal - childrenCost, 0);
}

export function getSelfAvgMetric(node: Node, field: NodeField): number {
  if (!(field in node)) {
    return undefined;
  }
  const selfTotal = getSelfTotalMetric(node, field);
  const isEstimate = isEstimateField(field);
  const loops = getNodeLoops(node, isEstimate);
  return selfTotal / loops;
}

export function getSelfWorkerTotalMetric(
  node: Node,
  field: NodeField,
  workerId: number,
): number {
  if (!(field in node)) {
    return undefined;
  }
  const workerCount = getWorkerCount(node);
  if (workerCount == 0) {
    return 0;
  }
  const cumulativeWorkerTotal = getCumulativeWorkerTotalMetric(
    node,
    field,
    workerId,
  );
  const workerTotalInChildren =
    node.Plans?.reduce((tot, child) => {
      const workerTotalInChild = getCumulativeWorkerTotalMetric(
        child,
        field,
        workerId,
      );
      return tot + workerTotalInChild;
    }, 0) ?? 0;

  // As above, sometimes child cost is larger than self cost; avoid negatives
  return Math.max(cumulativeWorkerTotal - workerTotalInChildren, 0);
}

export function getSelfWorkerAvgMetric(
  node: Node,
  field: NodeField,
  workerId: number,
): number {
  if (!(field in node)) {
    return undefined;
  }
  const workerCount = getWorkerCount(node);
  if (workerCount === 0) {
    return 0;
  }
  const workerSelfTotal = getSelfWorkerTotalMetric(node, field, workerId);
  const workerLoops = getWorkerLoops(node, workerId);
  return workerSelfTotal / workerLoops;
}

export function getSelfLeaderTotalMetric(node: Node, field: NodeField): number {
  if (!(field in node)) {
    return undefined;
  }
  const leaderTotal = getCumulativeLeaderTotalMetric(node, field);
  const leaderTotalInChildren =
    node.Plans?.reduce((tot, child) => {
      const childLeaderTotal = getCumulativeLeaderTotalMetric(child, field);
      return tot + childLeaderTotal;
    }, 0) ?? 0;

  // As above, avoid negative self-costs
  return Math.max(leaderTotal - leaderTotalInChildren, 0);
}

export function getSelfLeaderAvgMetric(node: Node, field: NodeField): number {
  if (!(field in node)) {
    return undefined;
  }
  const leaderSelfTotal = getSelfLeaderTotalMetric(node, field);
  const leaderLoops = getLeaderLoops(node);
  return leaderSelfTotal / leaderLoops;
}

export function getMaxParallelSelfTotalMetric(node: Node, field: NodeField) {
  if (!(field in node)) {
    return undefined;
  }
  const workerCosts =
    node.Workers?.map((w) => {
      return getSelfWorkerTotalMetric(node, field, w["Worker Number"]);
    }) ?? [];
  const leaderCost = getSelfLeaderTotalMetric(node, field);

  return Math.max(leaderCost, ...workerCosts);
}

function isPerLoopField(field: NodeField) {
  // When a node has Parent Relationship: Inner, it is executed repeatedly and
  // some of its metrics are reported as a per-loop average
  const PER_LOOP_METRICS: NodeField[] = [
    // planning metrics
    "Plan Rows",
    "Startup Cost",
    "Total Cost",

    // execution metrics
    "Actual Rows",
    "Actual Startup Time",
    "Actual Total Time",

    // TODO: Are these also per-loop? See show_instrumentation_count in explain.c
    "Rows Removed by Conflict Filter",
    "Rows Removed by Filter",
    "Rows Removed by Index Recheck",
    "Rows Removed by Join Filter",
  ];
  return PER_LOOP_METRICS.includes(field);
}

function isEstimateField(field: NodeField) {
  return ["Plan Rows", "Startup Cost", "Total Cost", "Plan Width"].includes(
    field,
  );
}
